export enum Role {
    CANDIDATE = 'CANDIDATE',
    COMPLETED_ASSESSMENT = 'COMPLETED_ASSESSMENT',
    ACADEMIC_CONSULTATION = 'ACADEMIC_CONSULTATION',
    EXTERNAL_CLIENT = 'EXTERNAL_CLIENT',
    SUPER_SUPER_ADMIN = 'SUPER_SUPER_ADMIN',
    SUPER_ADMIN = 'SUPER_ADMIN',
    ADMIN = 'ADMIN',
    CONSULTANT = 'CONSULTANT',
    BACKOFFICE_ADMIN = 'BACKOFFICE_ADMIN',
    BACKOFFICE_CONSULTANT = 'BACKOFFICE_CONSULTANT',
    SALES = 'SALES',
}

export const CANDIDATE_ROLES: string[] = [
    Role.CANDIDATE,
    Role.COMPLETED_ASSESSMENT,
    Role.ACADEMIC_CONSULTATION,
    Role.EXTERNAL_CLIENT,
];

export const SERVICE_PROVIDER_ROLES: string[] = [
    Role.SUPER_SUPER_ADMIN,
    Role.SUPER_ADMIN,
    Role.ADMIN,
    Role.CONSULTANT,
    Role.BACKOFFICE_ADMIN,
    Role.BACKOFFICE_CONSULTANT,
    Role.SALES,
];

// Roles that operate on another candidate's records (sales-style flows):
// GETs / POSTs hit /candidate_info/<Table>/<candidateId> instead of
// /candidate_info/<Table> (self). Keep this list in sync across FormBox and
// every form component that branches on isSalesPerson — the two used to
// drift, which silently broke data loading for ADMIN/CONSULTANT.
export const SALES_PERSON_ROLES: string[] = [
    Role.SALES,
    Role.BACKOFFICE_CONSULTANT,
    Role.BACKOFFICE_ADMIN,
    Role.ADMIN,
    Role.CONSULTANT,
    Role.SUPER_ADMIN,
    Role.SUPER_SUPER_ADMIN,
];

export const isSalesPersonRole = (role: string | null | undefined): boolean =>
    !!role && SALES_PERSON_ROLES.includes(role);

export const ADDITIONAL_CONSULTANT_ROLES: string[] = ['', Role.CONSULTANT, Role.ADMIN];

export const EDITABLE_TARGET_ROLES: { [key in Role]: Role[] } = {
    [Role.SUPER_SUPER_ADMIN]: [
        Role.SUPER_SUPER_ADMIN,
        Role.SUPER_ADMIN,
        Role.ADMIN,
        Role.CONSULTANT,
        Role.BACKOFFICE_ADMIN,
        Role.BACKOFFICE_CONSULTANT,
        Role.SALES,
    ],
    [Role.SUPER_ADMIN]: [
        Role.SUPER_ADMIN,
        Role.ADMIN,
        Role.CONSULTANT,
        Role.BACKOFFICE_ADMIN,
        Role.BACKOFFICE_CONSULTANT,
        Role.SALES,
    ],
    [Role.BACKOFFICE_ADMIN]: [Role.BACKOFFICE_ADMIN, Role.BACKOFFICE_CONSULTANT, Role.SALES],
    [Role.ADMIN]: [],
    [Role.CONSULTANT]: [],
    [Role.BACKOFFICE_CONSULTANT]: [],
    [Role.SALES]: [],
    [Role.CANDIDATE]: [],
    [Role.COMPLETED_ASSESSMENT]: [],
    [Role.ACADEMIC_CONSULTATION]: [],
    [Role.EXTERNAL_CLIENT]: [],
};

export const canEditUserRole = (
    currentUserRole: string | null | undefined,
    targetUserRole: string | null | undefined
): boolean => {
    if (!currentUserRole) return false;
    const editable = EDITABLE_TARGET_ROLES[currentUserRole as Role];
    if (!editable || editable.length === 0) return false;
    if (!targetUserRole) {
        return currentUserRole === Role.SUPER_SUPER_ADMIN || currentUserRole === Role.SUPER_ADMIN;
    }
    return editable.includes(targetUserRole as Role);
};

export const getAssignableRoles = (currentUserRole: string | null | undefined): Role[] => {
    if (!currentUserRole) return [];
    return EDITABLE_TARGET_ROLES[currentUserRole as Role] ?? [];
};

export const ADMIN_ROLES: Role[] = [
    Role.SUPER_SUPER_ADMIN,
    Role.SUPER_ADMIN,
    Role.ADMIN,
    Role.BACKOFFICE_ADMIN,
];

/** Roles that manage their own consultant licenses from My Account. */
export const LICENSE_MENU_ROLES: string[] = [
    Role.CONSULTANT,
    Role.ADMIN,
    Role.SUPER_ADMIN,
    Role.SUPER_SUPER_ADMIN,
];

export const CLIENT_TRANSFER_ADMIN_ROLES = ADMIN_ROLES;

export const canTransferClientOrganization = (
    currentUserRole: string | null | undefined
): boolean => !!currentUserRole && CLIENT_TRANSFER_ADMIN_ROLES.includes(currentUserRole as Role);

export const canEditUserProfile = (
    currentUserRole: string | null | undefined,
    targetUserRole: string | null | undefined
): boolean => {
    if (!currentUserRole) return false;
    if (
        targetUserRole &&
        CANDIDATE_ROLES.includes(targetUserRole) &&
        SERVICE_PROVIDER_ROLES.includes(currentUserRole)
    ) {
        return true;
    }
    return canEditUserRole(currentUserRole, targetUserRole);
};

type PermissionsMap = { [key in Role]: Role[] | string[] };
export const PERMISSIONS_TO_ASSIGN_USER_ROLES: PermissionsMap = {
    [Role.SUPER_SUPER_ADMIN]: [
        '',
        Role.SUPER_ADMIN,
        Role.ADMIN,
        Role.CONSULTANT,
        Role.BACKOFFICE_ADMIN,
        Role.BACKOFFICE_CONSULTANT,
        Role.SALES,
    ],
    [Role.SUPER_ADMIN]: [
        '',
        Role.ADMIN,
        Role.CONSULTANT,
        Role.BACKOFFICE_ADMIN,
        Role.BACKOFFICE_CONSULTANT,
        Role.SALES,
    ],
    [Role.ADMIN]: ['', Role.ADMIN, Role.CONSULTANT],
    [Role.CONSULTANT]: ['', Role.CONSULTANT],
    [Role.BACKOFFICE_ADMIN]: ['', Role.BACKOFFICE_ADMIN, Role.BACKOFFICE_CONSULTANT, Role.SALES],
    [Role.BACKOFFICE_CONSULTANT]: ['', Role.BACKOFFICE_CONSULTANT],
    [Role.SALES]: ['', Role.SALES],
    [Role.CANDIDATE]: [],
    [Role.COMPLETED_ASSESSMENT]: [],
    [Role.ACADEMIC_CONSULTATION]: [],
    [Role.EXTERNAL_CLIENT]: [],
};
